import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useToast } from 'vue-toastification'
import ServicesService from '@/services/servicesService'
import type {
  Service,
  ServiceListItem,
  ServiceCategory,
  ServiceSearchRequest,
  ServiceSearchResponse,
  ServiceReview
} from '@/types/services'

export const useServicesStore = defineStore('services', () => {
  // State
  const services = ref<ServiceListItem[]>([])
  const featuredServices = ref<ServiceListItem[]>([])
  const categories = ref<ServiceCategory[]>([])
  const currentService = ref<Service | null>(null)
  const serviceReviews = ref<ServiceReview[]>([])
  const isLoading = ref(false)
  const searchLoading = ref(false)
  const searchResults = ref<ServiceSearchResponse | null>(null)
  
  // Search/Filter state
  const searchTerm = ref('')
  const providerId = ref<string | null>(null)
  const categoryIds = ref<string[]>([])
  const minPrice = ref<number | null>(null)
  const maxPrice = ref<number | null>(null)
  const sortBy = ref<'price' | 'rating' | 'newest' | 'name' | 'distance'>('newest')
  const currentPage = ref(1)
  const pageSize = ref(10)

  // Location state
  const userLatitude = ref<number | null>(null)
  const userLongitude = ref<number | null>(null)
  const locationRadius = ref(50) // Default 50km radius
  const locationSearchTerm = ref<string | null>(null) // Text-based location search

  // Toast
  const toast = useToast()

  // Getters
  const totalPages = computed(() => {
    if (!searchResults.value) return 1
    return Math.ceil(searchResults.value.totalCount / pageSize.value)
  })

  const hasNextPage = computed(() => currentPage.value < totalPages.value)
  const hasPreviousPage = computed(() => currentPage.value > 1)

  const filteredCategories = computed(() => 
    categories.value.filter(category => category.isActive)
  )

  const searchQuery = computed<ServiceSearchRequest>(() => ({
    searchTerm: searchTerm.value || undefined,
    providerId: providerId.value || undefined,
    categoryIds: categoryIds.value.length > 0 ? categoryIds.value : undefined,
    minPrice: minPrice.value || undefined,
    maxPrice: maxPrice.value || undefined,
    locationSearchTerm: locationSearchTerm.value || undefined,
    latitude: userLatitude.value || undefined,
    longitude: userLongitude.value || undefined,
    radiusKm: (userLatitude.value && userLongitude.value) ? locationRadius.value : undefined,
    page: currentPage.value,
    pageSize: pageSize.value,
    sortBy: getSortField(sortBy.value),
    sortDirection: getSortDirection(sortBy.value)
  }))

  // Actions
  async function fetchServices(append: boolean = false, request?: Partial<ServiceSearchRequest>): Promise<boolean> {
    searchLoading.value = true

    try {
      const searchRequest = {
        ...searchQuery.value,
        ...request
      }

      console.log('🔍 fetchServices - searchRequest:', searchRequest)
      console.log('🔍 fetchServices - providerId in request:', searchRequest.providerId)
      console.log('🔍 fetchServices - append mode:', append)

      let response

      // Use dedicated provider endpoint if providerId is present
      if (searchRequest.providerId) {
        console.log('🔍 Using dedicated provider endpoint for providerId:', searchRequest.providerId)
        response = await ServicesService.getProviderServices(
          searchRequest.providerId,
          searchRequest.page || 1,
          searchRequest.pageSize || 10,
          searchRequest.sortBy || 'CreatedAt',
          searchRequest.sortDirection || 'desc'
        )
      } else {
        response = await ServicesService.searchServices(searchRequest)
      }

      searchResults.value = response

      // Append or replace services based on mode
      if (append) {
        services.value = [...services.value, ...response.services]
      } else {
        services.value = response.services
      }

      categories.value = response.categories

      return true
    } catch (error: any) {
      console.error('Failed to fetch services:', error)
      toast.error(error.response?.data?.message || 'Failed to load services')
      return false
    } finally {
      searchLoading.value = false
    }
  }

  async function fetchFeaturedServices(count: number = 6): Promise<boolean> {
    isLoading.value = true
    
    try {
      const services = await ServicesService.getFeaturedServices(count)
      featuredServices.value = services
      return true
    } catch (error: any) {
      console.error('Failed to fetch featured services:', error)
      toast.error('Failed to load featured services')
      return false
    } finally {
      isLoading.value = false
    }
  }

  // Helper function to detect if a string is a valid UUID
  function isUUID(str: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    return uuidRegex.test(str)
  }

  async function fetchServiceById(idOrCode: string): Promise<boolean> {
    console.log('🏪 Store fetchServiceById called with:', idOrCode)
    isLoading.value = true
    currentService.value = null // Clear current service while loading

    try {
      let service

      // Determine if this is a UUID or a service code
      if (isUUID(idOrCode)) {
        console.log('🏪 Detected UUID, fetching by ID')
        service = await ServicesService.getServiceById(idOrCode)
      } else {
        console.log('🏪 Detected service code, fetching by code')
        service = await ServicesService.getServiceByCode(idOrCode)
      }

      console.log('🏪 Store received service from API:', service)
      currentService.value = service
      console.log('🏪 Store currentService.value set to:', currentService.value)
      return true
    } catch (error: any) {
      console.error('❌ Store failed to fetch service:', error)

      // More specific error messages
      let errorMessage = 'Service could not be loaded'
      if (error.response?.status === 404) {
        errorMessage = 'Service not found'
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error - please try again later'
      } else if (error.code === 'ECONNREFUSED') {
        errorMessage = 'Cannot connect to server - please check if the backend is running'
      }

      toast.error(errorMessage)
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function fetchServiceReviews(serviceId: string, page: number = 1, pageSize: number = 10): Promise<boolean> {
    try {
      const reviews = await ServicesService.getServiceReviews(serviceId, page, pageSize)
      serviceReviews.value = reviews
      return true
    } catch (error: any) {
      console.error('Failed to fetch service reviews:', error)
      // For reviews, we'll gracefully handle errors since they're not critical to the service page
      serviceReviews.value = []
      return false
    }
  }

  async function fetchCategories(): Promise<boolean> {
    try {
      const fetchedCategories = await ServicesService.getCategories()
      categories.value = fetchedCategories
      return true
    } catch (error: any) {
      console.error('Failed to fetch categories:', error)
      toast.error('Failed to load categories')
      return false
    }
  }

  // Search and filter actions
  function updateSearchTerm(term: string) {
    searchTerm.value = term
    currentPage.value = 1 // Reset to first page
  }

  function updateProviderId(id: string | null) {
    providerId.value = id
    currentPage.value = 1 // Reset to first page
  }



  function updateSort(sort: typeof sortBy.value) {
    sortBy.value = sort
    currentPage.value = 1 // Reset to first page
  }

  function updateCategoryFilter(catIds: string[]) {
    categoryIds.value = catIds
    currentPage.value = 1 // Reset to first page
  }

  function updatePriceFilter(min: number | null, max: number | null) {
    minPrice.value = min
    maxPrice.value = max
    currentPage.value = 1 // Reset to first page
  }

  function updatePageSize(size: number) {
    pageSize.value = size
    currentPage.value = 1 // Reset to first page
  }

  function goToPage(page: number) {
    if (page >= 1 && page <= totalPages.value) {
      currentPage.value = page
    }
  }

  function nextPage() {
    if (hasNextPage.value) {
      currentPage.value++
    }
  }

  function previousPage() {
    if (hasPreviousPage.value) {
      currentPage.value--
    }
  }

  function resetPage() {
    currentPage.value = 1
  }

  async function clearFilters() {
    searchTerm.value = ''
    providerId.value = null
    categoryIds.value = []
    minPrice.value = null
    maxPrice.value = null
    sortBy.value = 'newest'
    currentPage.value = 1
    // Automatically fetch all services after clearing filters
    await fetchServices()
  }

  // Helper functions
  function getSortField(sort: string): string {
    switch (sort) {
      case 'price':
        return 'Price'
      case 'price_desc':
        return 'Price'
      case 'rating':
        return 'AverageRating'
      case 'name':
        return 'Name'
      case 'distance':
        return 'Distance'
      case 'trending':
        return 'trending'
      case 'in_demand':
        return 'in_demand'
      case 'newest':
      default:
        return 'CreatedAt'
    }
  }

  function getSortDirection(sort: string): 'asc' | 'desc' {
    switch (sort) {
      case 'price':
      case 'name':
      case 'distance':
        return 'asc'
      case 'price_desc':
      case 'rating':
      case 'newest':
      default:
        return 'desc'
    }
  }

  function clearCurrentService() {
    currentService.value = null
    serviceReviews.value = []
  }

  // Location methods
  function setUserLocation(latitude: number, longitude: number) {
    userLatitude.value = latitude
    userLongitude.value = longitude
    locationSearchTerm.value = null // Clear text search when using coordinates
  }

  function clearUserLocation() {
    userLatitude.value = null
    userLongitude.value = null
    locationSearchTerm.value = null
  }

  function setLocationRadius(radius: number) {
    locationRadius.value = radius
  }

  function setLocationSearchTerm(term: string | null) {
    locationSearchTerm.value = term
    // Clear coordinates when using text search
    if (term) {
      userLatitude.value = null
      userLongitude.value = null
    }
  }

  return {
    // State
    services,
    featuredServices,
    categories,
    currentService,
    serviceReviews,
    isLoading,
    searchLoading,
    searchResults,
    searchTerm,
    providerId,
    categoryIds,
    minPrice,
    maxPrice,
    sortBy,
    currentPage,
    pageSize,
    userLatitude,
    userLongitude,
    locationRadius,
    locationSearchTerm,

    // Getters
    totalPages,
    hasNextPage,
    hasPreviousPage,
    filteredCategories,
    searchQuery,

    // Actions
    fetchServices,
    fetchFeaturedServices,
    fetchServiceById,
    fetchServiceReviews,
    fetchCategories,
    updateSearchTerm,
    updateProviderId,
    updateCategoryFilter,
    updatePriceFilter,
    updatePageSize,
    updateSort,
    goToPage,
    nextPage,
    previousPage,
    resetPage,
    clearFilters,
    clearCurrentService,

    // Location methods
    setUserLocation,
    clearUserLocation,
    setLocationRadius,
    setLocationSearchTerm
  }
})